/** Line extension (amount) at 2 dp; purchase unit rate at 4 dp; sales unit rate at 7 dp (QBO-style). */

export const round2 = (n: number) => Math.round(n * 100) / 100;

export const round4 = (n: number) => Math.round(n * 10000) / 10000;

export const round7 = (n: number) => Number(n.toFixed(7));

export type LinePriceSource = 'rate' | 'amount';

export interface LinePricingFields {
  qty: number;
  unit_price: number;
  line_amount: number | null;
  price_source: LinePriceSource;
}

export const rateFromAmount = (amount: number, qty: number) =>
  qty > 0 ? round4(amount / qty) : 0;

export const lineGross = (line: LinePricingFields) =>
  line.price_source === 'amount' && line.line_amount != null
    ? line.line_amount
    : round2(line.qty * line.unit_price);

export const displayRate = (unit_price: number) => round4(unit_price);

export const formatLineAmountDisplay = (line: LinePricingFields, active: boolean): string => {
  if (!active) return '';
  if (line.price_source === 'amount' && line.line_amount != null) {
    return String(line.line_amount);
  }
  return String(lineGross(line));
};

export const commitAmountEntry = (
  raw: string,
  qty: number
): Pick<LinePricingFields, 'line_amount' | 'unit_price' | 'price_source'> | null => {
  if (!qty || qty < 0.0001) return null;
  const parsed = raw.trim() === '' ? 0 : Number(raw);
  if (Number.isNaN(parsed)) return null;
  const line_amount = round2(parsed);
  return {
    line_amount,
    unit_price: rateFromAmount(line_amount, qty),
    price_source: 'amount',
  };
};

/** Qty changes always keep rate; amount becomes qty × rate. */
export const qtyChangePricingPatch = (
  _line: LinePricingFields,
  qty: number
): Partial<LinePricingFields> => ({
  qty,
  line_amount: null,
  price_source: 'rate',
});

export const rateChangePricingPatch = (unit_price: number): Partial<LinePricingFields> => ({
  unit_price: round4(unit_price),
  line_amount: null,
  price_source: 'rate',
});

export const payloadUnitPrice = (line: LinePricingFields) => round4(line.unit_price);

export const applyAmountDraftsToLines = <T extends LinePricingFields>(
  lines: T[],
  drafts: Record<number, string>
): T[] => {
  if (Object.keys(drafts).length === 0) return lines;
  return lines.map((line, index) => {
    const raw = drafts[index];
    if (raw === undefined) return line;
    const committed = commitAmountEntry(raw, line.qty);
    if (!committed) return line;
    return { ...line, ...committed };
  });
};

/** Sales invoice line rate helpers — 7 dp precision. */

export const salesRateFromAmount = (amount: number, qty: number) =>
  qty > 0 ? round7(amount / qty) : 0;

export const salesDisplayRate = (unit_price: number) => round7(unit_price);

export const salesCommitAmountEntry = (
  raw: string,
  qty: number
): Pick<LinePricingFields, 'line_amount' | 'unit_price' | 'price_source'> | null => {
  if (!qty || qty < 0.0001) return null;
  const parsed = raw.trim() === '' ? 0 : Number(raw);
  if (Number.isNaN(parsed)) return null;
  const line_amount = round2(parsed);
  return {
    line_amount,
    unit_price: salesRateFromAmount(line_amount, qty),
    price_source: 'amount',
  };
};

/** Qty changes always keep rate; amount becomes qty × rate. */
export const salesQtyChangePricingPatch = (
  _line: LinePricingFields,
  qty: number
): Partial<LinePricingFields> => ({
  qty,
  line_amount: null,
  price_source: 'rate',
});

export const salesRateChangePricingPatch = (
  unit_price: number
): Partial<LinePricingFields> => ({
  unit_price: round7(unit_price),
  line_amount: null,
  price_source: 'rate',
});

export const salesPayloadUnitPrice = (line: LinePricingFields) => round7(line.unit_price);

export const salesApplyAmountDraftsToLines = <T extends LinePricingFields>(
  lines: T[],
  drafts: Record<number, string>
): T[] => {
  if (Object.keys(drafts).length === 0) return lines;
  return lines.map((line, index) => {
    const raw = drafts[index];
    if (raw === undefined) return line;
    const committed = salesCommitAmountEntry(raw, line.qty);
    if (!committed) return line;
    return { ...line, ...committed };
  });
};
